import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardLink, EmptyState, Icon, Input, PageHeader } from '../../../components/ui';
import { StatusBadge } from '../../../components/common/Parts/Parts';
import { useAuth } from '../../../app/providers/AuthProvider';
import { cn } from '../../../utils/cn';
import { labelOf } from '../../../utils/labels';
import { BUSINESS_FLOWS, GUIDE, type GuideFlow, type GuideSection, type GuideTable } from '../constants/guideContent';
import type { StatusMap } from '../../../types/common';

/** Scrolls to a section without changing the route (the page is one long document). */
const jump = (id: string) => document.getElementById(`guide-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const Arrow = () => <Icon name="chevronRight" size={16} className="shrink-0 text-ink-300" aria-hidden="true" />;

/** The big picture: boxes joined by arrows, each opening its section. */
function BusinessFlows({ flows }: { flows: typeof BUSINESS_FLOWS }) {
  return (
    <Card className="p-5">
      <h2 className="text-[15px] font-semibold text-navy-900">How the business works</h2>
      <p className="mt-0.5 text-[13px] text-ink-400">Each box opens its part of the guide.</p>
      <div className="mt-4 space-y-4">
        {flows.map((f) => (
          <div key={f.title}>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-400">{f.title}</p>
            <ol className="flex flex-wrap items-center gap-2">
              {f.steps.map((s, i) => (
                <Fragment key={s.label}>
                  {i > 0 && <Arrow />}
                  <li>
                    <button
                      type="button"
                      onClick={() => jump(s.section)}
                      className="rounded-lg border border-line bg-white px-3 py-2 text-left outline-none transition-colors hover:border-brand-300 hover:bg-brand-50/40 focus-visible:ring-2 focus-visible:ring-steel"
                    >
                      <span className="block text-sm font-medium text-navy-900">{s.label}</span>
                      <span className="block text-xs text-ink-400">{s.note}</span>
                    </button>
                  </li>
                </Fragment>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Status badges in order with arrows, plus the side steps that can happen along the way. */
function Flow({ flow }: { flow: GuideFlow }) {
  return (
    <div className="rounded-lg bg-navy-50/60 p-4">
      <p className="mb-2.5 text-xs font-medium uppercase tracking-wide text-ink-400">Usual flow</p>
      <ol className="flex flex-wrap items-center gap-2">
        {flow.steps.map((s, i) => (
          <Fragment key={s}>
            {i > 0 && <Arrow />}
            <li><StatusBadge map={flow.map} value={s} /></li>
          </Fragment>
        ))}
      </ol>
      {flow.side?.length ? (
        <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-ink-500">
          {flow.side.map((s) => (
            <li key={s.value} className="flex items-center gap-2">
              <span>Can also be</span>
              <StatusBadge map={flow.map} value={s.value} />
              <span>{s.note}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function StatusTable({ table }: { table: GuideTable }) {
  const hasNext = table.rows.some((r) => r.next);
  return (
    <div>
      <h4 className="mb-2 text-sm font-semibold text-navy-900">{table.title}</h4>
      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead className="bg-navy-50/60 text-[13px] text-ink-500">
            <tr>
              <th scope="col" className="w-44 px-4 py-2.5 font-normal">{table.badges ? 'Status' : 'Name'}</th>
              <th scope="col" className="px-4 py-2.5 font-normal">What it means</th>
              {hasNext && <th scope="col" className="w-[32%] px-4 py-2.5 font-normal">What happens next</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {table.rows.map((r) => (
              <tr key={r.value} className="align-top">
                <td className="px-4 py-3">
                  {table.badges ? (
                    <StatusBadge map={table.map as StatusMap} value={r.value} />
                  ) : (
                    <span className="font-medium text-navy-900">{labelOf(table.map, r.value)}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-ink-600">{r.meaning}</td>
                {hasNext && <td className="px-4 py-3 text-ink-500">{r.next ?? ''}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Section({ s }: { s: GuideSection }) {
  return (
    <Card as="section" id={`guide-${s.id}`} aria-labelledby={`guide-${s.id}-h`} className="scroll-mt-24">
      <div className="flex items-start justify-between gap-3 px-5 pt-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Icon name={s.icon} size={18} />
          </span>
          <div className="min-w-0">
            <h3 id={`guide-${s.id}-h`} className="text-[17px] font-semibold text-navy-900">{s.title}</h3>
            <p className="mt-0.5 max-w-3xl text-sm text-ink-500">{s.summary}</p>
          </div>
        </div>
        <CardLink to={s.to}>Open</CardLink>
      </div>

      <div className="space-y-5 p-5">
        {s.flow && <Flow flow={s.flow} />}

        <div>
          <h4 className="mb-2 text-sm font-semibold text-navy-900">How it works</h4>
          <ol className="space-y-2">
            {s.howItWorks.map((step, i) => (
              <li key={step} className="flex gap-3 text-sm text-ink-600">
                <span className="tabular flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-600 text-[11px] font-semibold text-white">{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {s.tables.map((t) => <StatusTable key={t.title} table={t} />)}

        {s.tips?.length ? (
          <div className="rounded-lg border border-brand-100 bg-brand-50/50 p-4">
            <p className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-navy-900">
              <Icon name="info" size={16} className="text-brand-600" /> Good to know
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-ink-600">
              {s.tips.map((t) => <li key={t}>{t}</li>)}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/** Text of a section, for the search box. */
function sectionText(s: GuideSection): string {
  const tableText = s.tables.flatMap((t) => [t.title, ...t.rows.flatMap((r) => [labelOf(t.map, r.value), r.meaning, r.next ?? ''])]);
  return [s.title, s.summary, ...s.howItWorks, ...(s.tips ?? []), ...tableText].join(' ').toLowerCase();
}

/** Help & guide: what each module is for, how work moves through it and what every status means. */
export default function HelpGuide() {
  const { user } = useAuth();
  const role = user?.role ?? 'worker';
  const [q, setQ] = useState('');

  const mine = useMemo(() => GUIDE.filter((s) => s.roles.includes(role)), [role]);
  const flows = BUSINESS_FLOWS.filter((f) => f.roles.includes(role));
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = words.length ? mine.filter((s) => words.every((w) => sectionText(s).includes(w))) : mine;

  return (
    <>
      <PageHeader title="Help & guide" subtitle="What each screen is for, how work moves through it, and what every status means." />

      <div className="grid items-start gap-5 xl:grid-cols-[220px_minmax(0,1fr)]">
        <nav aria-label="Guide contents" className="hidden xl:sticky xl:top-24 xl:block">
          <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wide text-ink-400">Contents</p>
          <ul className="space-y-0.5">
            {mine.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => jump(s.id)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-steel',
                    shown.includes(s) ? 'text-ink-600 hover:text-navy-900' : 'text-ink-300',
                  )}
                >
                  <Icon name={s.icon} size={16} className="shrink-0" />
                  {s.title}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 space-y-5">
          <Input leftIcon="search" placeholder="Search the guide, e.g. “partly paid” or “advance”" aria-label="Search the guide" value={q} onChange={(e) => setQ(e.target.value)} />
          {!words.length && flows.length > 0 && <BusinessFlows flows={flows} />}
          {shown.length ? (
            shown.map((s) => <Section key={s.id} s={s} />)
          ) : (
            <Card>
              <EmptyState icon="search" title="Nothing found" message={<>No part of the guide mentions “{q.trim()}”. Try another word, or <Link to="/" className="text-brand-600 hover:underline">go to the dashboard</Link>.</>} />
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
